import { ThemeProvider } from '@emotion/react';
import { useRouter } from 'next/dist/client/router';
import Head from 'next/head';
import { useMemo } from 'react';
import Cookies from 'universal-cookie';

import Login from '../../../components/Login';
import Main from '../../../components/Main';
import { WWProvider } from '../../../components/WWContext';
import useThemeCoupleOverride from '../../../lib/useThemeCoupleOverride';

import {
  isWwsError,
  isWwsWeddingWebsiteV2,
  WeddingWebsiteQuery,
} from '../../../types/graphql';

import type { Theme } from '@emotion/react';
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from 'next';
import { makeGuestServicesAPIRequest } from '../../../lib/guest-fed-wws-result';
import { filterRouteScopedData } from '../../../lib/filterRouteScopedData';
import { isSinglePageLayoutFromQuery } from '../../../lib/isSinglePageLayout';

// newrelic is only available server-side
// eslint-disable-next-line @typescript-eslint/no-require-imports
const newrelic = typeof window === 'undefined' ? require('newrelic') : null;
import {
  useWWSQueryContext,
  WWSQueryProvider,
} from '../../../context/WWSQueryContext';

interface InitialProps {
  sessionToken?: string;
  pin?: string;
  initialWWSData: WeddingWebsiteQuery; // Replace `any` with the appropriate type for the fetched data
}

const SlugInnerContainer = function SlugInnerContainer() {
  const { data: initialWWSData } = useWWSQueryContext();
  const { query } = useRouter();

  // Check the theme or themeOverride for couple info, and override it in the WWS Data.
  const data = useThemeCoupleOverride(
    query['useThemeCouple'] === 'true',
    initialWWSData,
    query['themeOverride']
  );

  const theme = useMemo<Partial<Theme>>(() => {
    let styles: Partial<Theme> = {};

    const resultTheme = data?.weddingWebsiteResult?.theme;

    if (typeof resultTheme !== 'undefined' && resultTheme !== null) {
      const themeStyles = resultTheme.themeStyles;

      if (Array.isArray(themeStyles) && typeof themeStyles[0] !== 'undefined') {
        styles = themeStyles[0].styles;
      }
    }

    return styles;
  }, [data?.weddingWebsiteResult?.theme]);

  if (typeof data !== 'undefined' && isWwsError(data.weddingWebsiteResult)) {
    return (
      <ThemeProvider theme={theme}>
        <Login />
      </ThemeProvider>
    );
  }

  if (
    typeof data !== 'undefined' &&
    isWwsWeddingWebsiteV2(data.weddingWebsiteResult)
  ) {
    return (
      <WWProvider data={data}>
        <Main />
      </WWProvider>
    );
  }

  return (
    <>
      <Head>
        <meta name="googlebot" content="noindex,nofollow" />
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <div>
        Oops! Our system is having trouble right now. Please try again in a few
        minutes.
      </div>
    </>
  );
};

const Slug: NextPage<InitialProps> = function Slug(props: InitialProps) {
  return (
    <WWSQueryProvider initialData={props.initialWWSData}>
      <SlugInnerContainer />
    </WWSQueryProvider>
  );
};

export const getServerSideProps: GetServerSideProps<InitialProps> = async (
  context: GetServerSidePropsContext
) => {
  const cookies = new Cookies(context?.req?.headers.cookie);
  const sessionTokenCookie: string = cookies.get('xo-session-token') as string;
  const sessionToken: string =
    typeof sessionTokenCookie !== 'undefined' ? sessionTokenCookie : '';
  const pinCookie: string = cookies.get('wws-pin') as string;
  const pin: string | null =
    typeof pinCookie !== 'undefined' && pinCookie !== '' ? pinCookie : null;
  const slug = context?.query['slug'];

  try {
    if (context.res && context.res.writableEnded) {
      return {
        props: {
          initialWWSData: {} as WeddingWebsiteQuery,
        },
      };
    }
    if (
      typeof slug === 'undefined' ||
      Array.isArray(slug) ||
      slug.endsWith('.php')
    ) {
      throw new Error('Invalid slug');
    }

    delete context.req.headers['content-length'];

    const data = await makeGuestServicesAPIRequest(
      {
        pin: !!pin ? String(pin) : '',
        slug: context?.query['slug'] as string,
        themeId: context?.query['themeId'] as string | null,
        previewId: context?.query['previewId'] as string | null,
      },
      {
        sessionToken,
      },
      {
        ...context.req.headers,
        'content-type': 'application/json',
      }
    );

    const route = context?.query['route'];
    const routeName = Array.isArray(route) ? (route[0] ?? '') : route || '';

    const isSinglePageLayout = isSinglePageLayoutFromQuery(data, context.query);

    return {
      props: {
        initialWWSData: filterRouteScopedData(data, {
          routeName,
          isSinglePageLayout,
        }),
      },
    };
  } catch (error) {
    // Don't report intentional 404s (invalid slug) to New Relic
    if (error instanceof Error && error.message !== 'Invalid slug') {
      newrelic?.noticeError(error);
    }
    return {
      notFound: true,
    };
  }
};

export default Slug;
